import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { AppRoleProvider } from './hooks/useAppRole';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import PrivacyPage from './pages/PrivacyPage';
import ClassroomPage from './pages/ClassroomPage';
import NotFoundPage from './pages/NotFoundPage';

const SubjectChatRouteLayout = lazy(() => import('./components/layout/SubjectChatRouteLayout'));
const SubjectChatPage = lazy(() => import('./pages/SubjectChatPage'));
const AssistantChatPage = lazy(() => import('./pages/AssistantChatPage'));
const TeacherAdminLayout = lazy(() => import('./components/layout/TeacherAdminLayout'));
const TeacherChatbotsPage = lazy(() => import('./pages/TeacherChatbotsPage'));
const TeacherCategoriesPage = lazy(() => import('./pages/TeacherCategoriesPage'));
const TeacherVectorStoresPage = lazy(() => import('./pages/TeacherVectorStoresPage'));
const TeacherPrepSkillsPage = lazy(() => import('./pages/TeacherPrepSkillsPage'));
const TeacherFeedbackPage = lazy(() => import('./pages/TeacherFeedbackPage'));
const TeacherAccountsPage = lazy(() => import('./pages/TeacherAccountsPage'));
const TeacherLoginPage = lazy(() => import('./pages/TeacherLoginPage'));
const TeacherProfilePage = lazy(() => import('./pages/TeacherProfilePage'));
const OperatorLoginPage = lazy(() => import('./pages/OperatorLoginPage'));
const TeacherSignupPage = lazy(() => import('./pages/TeacherSignupPage'));
const TeacherAccessRequestPage = lazy(() => import('./pages/TeacherAccessRequestPage'));
const TeacherClassroomTokensPage = lazy(() => import('./pages/TeacherClassroomTokensPage'));
const TeacherClassroomPage = lazy(() => import('./pages/TeacherClassroomPage'));
const TeacherPrepPage = lazy(() => import('./pages/TeacherPrepPage'));

function LegacyTeacherAdminRedirect() {
  const location = useLocation();
  const next = location.pathname.replace(/^\/laerer(?=\/|$)/, '/admin');
  return <Navigate to={`${next}${location.search}${location.hash}`} replace />;
}

export function AppRoutes() {
  return (
    <AppRoleProvider>
      <Suspense fallback={<div className="route-loading" role="status">Indlæser…</div>}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/klasse" element={<Navigate to="/klassevaerelset" replace />} />
          <Route path="/klassevaerelset" element={<ClassroomPage />} />
          <Route element={<SubjectChatRouteLayout />}>
            <Route path="/matematik" element={<SubjectChatPage subjectId="matematik" />} />
            <Route path="/fysik" element={<SubjectChatPage subjectId="fysik" />} />
            <Route path="/kemi" element={<SubjectChatPage subjectId="kemi" />} />
            <Route path="/biologi" element={<SubjectChatPage subjectId="biologi" />} />
            <Route path="/assistent/:code" element={<AssistantChatPage />} />
          </Route>
          <Route path="/om-gymgpt" element={<AboutPage />} />
          <Route path="/privatlivspolitik" element={<PrivacyPage />} />
          <Route path="/til-klassevaerelset" element={<TeacherClassroomPage />} />
          <Route path="/laererforberedelse" element={<TeacherPrepPage />} />
          <Route path="/login" element={<TeacherLoginPage />} />
          <Route path="/profil" element={<TeacherProfilePage />} />
          <Route path="/admin-gymgpt-login" element={<OperatorLoginPage />} />
          <Route path="/opret-laerer" element={<TeacherSignupPage />} />
          <Route path="/ansog-om-adgang" element={<TeacherAccessRequestPage />} />
          <Route path="/admin" element={<TeacherAdminLayout />}>
            <Route index element={<Navigate to="chatbots" replace />} />
            <Route path="chatbots" element={<TeacherChatbotsPage />} />
            <Route path="klassekoder" element={<TeacherClassroomTokensPage />} />
            <Route path="categories" element={<TeacherCategoriesPage />} />
            <Route path="skills" element={<TeacherPrepSkillsPage />} />
            <Route path="feedback" element={<TeacherFeedbackPage />} />
            <Route path="laerere" element={<TeacherAccountsPage />} />
            <Route path="vector-stores" element={<TeacherVectorStoresPage />} />
          </Route>
          <Route path="/laerer/*" element={<LegacyTeacherAdminRedirect />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AppRoleProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
